import { Mesh, Object3D, Vector3 } from 'three';
import { ArticlesCreator } from '@project/components/article/presenter/article-creator';
import { NumberUtils } from '@shared/util/number-utils';
import { ArticleUsageInfoPropertyDef } from '@article/constants/article-usage-info-property-def';
import { ArticleContainerPropertyDef } from '@article/constants/article-container-property-def';
import { ArticleContainer } from '@3d-scene/model3d/article-container';
import { ArticleVO } from '@article/model/vo/article.vo';
import { TextureAreaVO } from '@shared/vo/meta-element.vo';
import { ArticleUsageUtils } from '@article/util/article-usage-utils';
import { Object3DUtils } from '@3d-scene/common/util/object-3d-utils';
import { Connection } from '@3d-scene/model3d/connection';
import { MetricUtils } from '@3d-scene/common/util/metric-utils';
import { ContainerService } from '@3d-scene/model3d/container.service';
import { FaceUtils } from '@3d-scene/common/util/face-utils';

export class CoverLabelStripPostParseProcessor {
  constructor(private _componentUtils: ContainerService) {}

  public process(container: ArticleContainer, article: ArticleVO): void {
    if (container.articleObject3DDataShared) {
      throw new Error('Modifying a shared article geometry is not allowed!');
    }
    const textureNames: string[] = [];
    for (const con of article.metaElements) {
      if (con instanceof TextureAreaVO) {
        textureNames.push(con.name);
      }
    }

    let stretchLength: number = ArticleUsageUtils.getArticleUsageInfoPropertyFloatValue(
      container.articleUsage,
      ArticleUsageInfoPropertyDef.ARTICLE_LENGTH
    );
    if (!NumberUtils.isNumber(stretchLength) && container.properties.containsKey(ArticleContainerPropertyDef.STRETCH_LENGTH)) {
      stretchLength = this.getStretchLength(container);
      ArticleUsageUtils.setArticleUsageInfoPropertyFloatValue(container.articleUsage, ArticleUsageInfoPropertyDef.ARTICLE_LENGTH, stretchLength);
    }

    let articleOffset: number = ArticleUsageUtils.getArticleUsageInfoPropertyFloatValue(
      container.articleUsage,
      ArticleContainerPropertyDef.RELATIVE_OFFSET
    );
    if (!NumberUtils.isNumber(articleOffset) && container.properties.containsKey(ArticleContainerPropertyDef.RELATIVE_OFFSET)) {
      articleOffset = this.getArticleOffset(container);
      ArticleUsageUtils.setArticleUsageInfoPropertyFloatValue(container.articleUsage, ArticleUsageInfoPropertyDef.ARTICLE_OFFSET, articleOffset);
    }

    if (container.model && NumberUtils.isNumber(stretchLength)) {
      this.changeMeshes(container, container.model, stretchLength, textureNames);
      // update text-connection-info for the given article-container since texture-connection has been re-sized
      container.recreateTextureConnectionInfos();
    }
  }

  private getStretchLength(container: ArticleContainer): number {
    return container.properties.getValue(ArticleContainerPropertyDef.STRETCH_LENGTH);
  }

  private getArticleOffset(container: ArticleContainer): number {
    return container.properties.getValue(ArticleContainerPropertyDef.RELATIVE_OFFSET);
  }

  private changeMeshes(articleContainer: ArticleContainer, parentContainer: Object3D, extrusionLength: number, textureNames: string[]): void {
    for (const child of Object3DUtils.getChildrenRecursively(parentContainer)) {
      this.changeMeshes(articleContainer, child as Object3D, extrusionLength, textureNames);

      const connection: Connection = articleContainer.findConnectionByName(child.name);
      if (connection) {
        if (textureNames.indexOf(child.name) === -1) {
          this.resizeConnection(articleContainer, connection, connection.localRectangle.width, extrusionLength);
        } else {
          const realSizeInLocal: Vector3 = MetricUtils.getRealSize(articleContainer.model, textureNames);
          const articleHeight: number = realSizeInLocal.z;
          this.resizeConnection(articleContainer, connection, extrusionLength, articleHeight);
          // lift mesh for correct handling mouse events
          FaceUtils.moveMeshByOffset(connection.mesh, new Vector3(0, 0.1, 0));
        }
      } else {
        // if mesh of dynamic article
        const extrusion: Mesh = ArticlesCreator.createDynamicArticle(child as Mesh, extrusionLength);
        parentContainer.remove(child);
        Object3DUtils.dispose(child);
        parentContainer.add(extrusion);
      }
    }
  }

  private resizeConnection(articleContainer: ArticleContainer, connection: Connection, connectionWidth: number, connectionHeight: number): void {
    const oldMesh: Mesh = connection.mesh;
    this._componentUtils.resizeConnectionFromStart(connection, connectionWidth, connectionHeight);
    articleContainer.model.remove(oldMesh);
    articleContainer.model.add(connection.mesh);
    articleContainer.updateMatrixWorld(true);
  }
}
